import { ComponentKind } from "~/graphql/supergraphEnums";
import { findAttributeByPath } from "../../../../shared/RulesSection/attributeCatalogue";
import type { AttributeLeaf } from "../../../../shared/RulesSection/attributeCatalogue";
import type { Formula } from "../../FormulasGrid";
import type { TypeHierarchy } from "../../HierarchyTreeGrid";
import type { GetFormulaDetailQuery } from "~/graphql/supergraphTypes";

/** Tied to the `GetFormulaDetail` selection so removing `components`
 *  from the document fails the build instead of silently emptying
 *  every expansion. */
export type FormulaDetail = NonNullable<GetFormulaDetailQuery["finpubFormula"]>;

type FormulaDetailComponent = FormulaDetail["components"][number];

/** Mirrors the backend's max component-tree depth (and the fragment unroll
 * in `queries.federation.gql`): components live at depths 1..10, so FORMULA
 * anchors sitting at depth 10 are not expanded further. */
export const MAX_COMPONENT_TREE_DEPTH = 10;

export type FormulaComponentAccordionItem = {
  /** Slash-joined chain of catalog FormulaComponent ids from the field's
   * formula down to the leaf (e.g. `"12/45"`); matches the keys of
   * `FieldSettingsValues["formulaComponents"]`. */
  pathKey: string;
  /** Leaf display name. Repeated usages of one leaf within the same parent
   * get an occurrence suffix (e.g. `"Gross Commitment (2)"`). */
  fieldLabel: string;
  /** Formula names along the anchor chain, root formula first. Repeated
   * usages of one sub-formula within the same parent get an occurrence
   * suffix (e.g. `"Sub Formula (2)"`). */
  formulaPath: ReadonlyArray<string>;
  /** Leaf kind — the Node types card renders only NODE leaves. */
  componentKind: ComponentKind.Node | ComponentKind.PrimaryField;
};

type HierarchyNode = {
  id?: number;
  name: string;
  children?: HierarchyNode[];
};

const walkHierarchyNodes = (
  node: HierarchyNode | undefined,
  namesById: Map<number, string>,
): void => {
  if (!node) return;
  if (node.id != null) {
    namesById.set(node.id, node.name);
  }
  for (const child of node.children ?? []) {
    walkHierarchyNodes(child, namesById);
  }
};

const buildNodeNameLookup = (
  typeHierarchy: TypeHierarchy | null | undefined,
): Map<number, string> => {
  const namesById = new Map<number, string>();
  walkHierarchyNodes(typeHierarchy?.tree_structure?.root, namesById);
  return namesById;
};

const buildFormulaNameLookup = (formulas: ReadonlyArray<Formula>): Map<number, string> => {
  const namesById = new Map<number, string>();
  for (const formula of formulas) {
    namesById.set(Number(formula.id), formula.name);
  }
  return namesById;
};

const resolvePrimaryFieldLabel = (
  referencedPrimaryField: string,
  attributeLeaves: ReadonlyArray<AttributeLeaf>,
): string => {
  const attribute = findAttributeByPath(referencedPrimaryField, attributeLeaves);
  return attribute?.label ?? referencedPrimaryField;
};

/** Distinct sub-formula ids referenced by `detail`, for the hook's
 * breadth-first detail fetch. */
export const collectReferencedFormulaIds = (detail: FormulaDetail): number[] => {
  const ids = new Set<number>();
  for (const component of detail.components) {
    if (
      component.componentKind === ComponentKind.Formula &&
      component.referencedFormulaId != null
    ) {
      ids.add(component.referencedFormulaId);
    }
  }
  return [...ids];
};

const resolveLeafLabel = (
  component: FormulaDetailComponent,
  nodeNamesById: Map<number, string>,
  attributeLeaves: ReadonlyArray<AttributeLeaf>,
): string => {
  if (component.componentKind === ComponentKind.Node && component.referencedNodeId != null) {
    return nodeNamesById.get(component.referencedNodeId) ?? String(component.referencedNodeId);
  }
  if (
    component.componentKind === ComponentKind.PrimaryField &&
    component.referencedPrimaryField != null
  ) {
    return resolvePrimaryFieldLabel(component.referencedPrimaryField, attributeLeaves);
  }
  return component.label;
};

/** Expands a formula's component tree into one accordion item per leaf
 * occurrence (NODE / PRIMARY_FIELD), depth-first in `sortOrder` order so
 * items read in expression order. FORMULA components are anchors: they
 * contribute a path-key segment and a `formulaPath` name but no item of
 * their own. Anchors are skipped when the referenced formula's detail has
 * not loaded (fetch still in flight, archived, or beyond the max tree
 * depth) or when the depth guard trips. */
export const buildFormulaComponentAccordionItems = (
  rootFormulaId: number,
  detailsByFormulaId: ReadonlyMap<number, FormulaDetail>,
  typeHierarchy: TypeHierarchy | null | undefined,
  attributeLeaves: ReadonlyArray<AttributeLeaf>,
  formulas: ReadonlyArray<Formula>,
): ReadonlyArray<FormulaComponentAccordionItem> => {
  const rootDetail = detailsByFormulaId.get(rootFormulaId);
  if (!rootDetail) return [];

  const nodeNamesById = buildNodeNameLookup(typeHierarchy);
  const formulaNamesById = buildFormulaNameLookup(formulas);
  const resolveFormulaName = (formulaId: number, detail: FormulaDetail): string =>
    formulaNamesById.get(formulaId) ?? detail.name;

  const items: FormulaComponentAccordionItem[] = [];

  // Identity of the referenced target — repeated usages of one target
  // within the same parent are distinct occurrences (distinct component
  // ids) and get suffixed names so their items stay distinguishable.
  const componentTargetKey = (component: FormulaDetailComponent): string => {
    if (component.componentKind === ComponentKind.Formula) {
      return `f:${component.referencedFormulaId}`;
    }
    if (component.componentKind === ComponentKind.Node) {
      return `n:${component.referencedNodeId}`;
    }
    if (component.componentKind === ComponentKind.PrimaryField) {
      return `p:${component.referencedPrimaryField}`;
    }
    return `l:${component.label}`;
  };

  const expandFormula = (
    detail: FormulaDetail,
    pathPrefix: string,
    formulaPath: ReadonlyArray<string>,
    depth: number,
  ): void => {
    const components = [...detail.components].sort((a, b) => a.sortOrder - b.sortOrder);

    const usageCounts = new Map<string, number>();
    for (const component of components) {
      const key = componentTargetKey(component);
      usageCounts.set(key, (usageCounts.get(key) ?? 0) + 1);
    }
    const occurrenceCounters = new Map<string, number>();
    const nextOccurrenceSuffix = (key: string): string => {
      if ((usageCounts.get(key) ?? 0) <= 1) return "";
      const occurrence = (occurrenceCounters.get(key) ?? 0) + 1;
      occurrenceCounters.set(key, occurrence);
      return ` (${occurrence})`;
    };

    for (const component of components) {
      const pathKey = pathPrefix ? `${pathPrefix}/${component.id}` : String(component.id);
      const targetKey = componentTargetKey(component);

      if (component.componentKind === ComponentKind.Formula) {
        const subFormulaId = component.referencedFormulaId;
        if (subFormulaId == null || depth >= MAX_COMPONENT_TREE_DEPTH) continue;
        const subDetail = detailsByFormulaId.get(subFormulaId);
        if (!subDetail) continue;

        const anchorName =
          resolveFormulaName(subFormulaId, subDetail) + nextOccurrenceSuffix(targetKey);
        expandFormula(subDetail, pathKey, [...formulaPath, anchorName], depth + 1);
        continue;
      }

      const fieldLabel =
        resolveLeafLabel(component, nodeNamesById, attributeLeaves) +
        nextOccurrenceSuffix(targetKey);
      // Default to PRIMARY_FIELD, not NODE: a leaf kind the supergraph adds
      // later would otherwise land in the Node types card, where the save
      // path narrows its persisted field type to VALUE / QUANTITY. Treating
      // it as a primary field round-trips that type untouched.
      const componentKind =
        component.componentKind === ComponentKind.Node
          ? ComponentKind.Node
          : ComponentKind.PrimaryField;
      items.push({
        pathKey,
        fieldLabel,
        formulaPath,
        componentKind,
      });
    }
  };

  expandFormula(rootDetail, "", [resolveFormulaName(rootFormulaId, rootDetail)], 1);
  return items;
};
